import {
    Box,
    Button,
    FormControl,
    FormControlProps,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from "@mui/material";
import { PropsWithChildren, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { methodGetAvgScore, methodGetHomeSummary } from "../../../../../api/apisumm/api";
import {
    SelectEnumValueProps,
    useSelectEnumValue,
} from "../../../../../component/basic/inputs/select/SelectEnum";
import { CardBox } from "../../../../../component/basic/surfaces/box/CardBox";
import { XStack } from "../../../../../component/basic/surfaces/box/XStack";
import { EnumDisplay } from "../../../../../model/Enum";
import { FilterScopeHomeItems, FilterScopeKind } from "../../../../../model/exam/Exam";
import { RoleKind } from "../../../../../model/Role";
import { RateScore } from "../../../../../model/statistic/Count";
import { UserDaily } from "../../../../../model/statistic/Daily";
import { HomeSummary, Simulate } from "../../../../../model/statistic/summary/summary_data";
import { useAppDispatch } from "../../../../../store/hooks";
import { ensureDaily } from "../../../../../store/root/ensure";
import { selectSummaryBank } from "../../../../../store/root/selector";
import { selectLogin, selectRoleKind, selectUser } from "../../../../../store/user/selector";
import { ColorOfficialRed, Colors } from "../../../../../tools/color";
import { OpenURL } from "../../../../../tools/url";
import { useAPIRsp } from "../../../../../tools/use_api_data";
import { Calendar } from "./Calendar";

export function TodayCalendar() {
    const dispatch = useAppDispatch();

    const bank = useSelector(selectSummaryBank);
    const role = useSelector(selectRoleKind);

    const daily = bank.user_daily;

    useEffect(() => {
        ensureDaily(daily, dispatch);
    }, [daily, dispatch]);

    return (
        <CardBox
            sx={{
                width: "50%",
                margin: "10px",
                display: "flex",
                alignItems: "center",
                position: "relative",
            }}
        >
            <DakaLogo />
            <ZuotiDisplay daily={daily} />
            <Button
                sx={{ position: "absolute", right: "20px", bottom: "20px" }}
                onClick={() => {
                    OpenURL("/nav/analyze");
                }}
                disabled={role < RoleKind.FreeVIP}
            >
                模考数据
            </Button>
        </CardBox>
    );
}

interface ZuotiDisplayProps {
    daily: UserDaily;
}
export function ZuotiDisplay(props: ZuotiDisplayProps) {
    const { daily } = props;

    const user = useSelector(selectUser);

    const [summRsp] = useAPIRsp(methodGetHomeSummary, {}, { summary: new HomeSummary() });
    const summary = summRsp.summary;

    return (
        <Stack
            direction="row"
            sx={{
                width: "100%",
                justifyContent: "space-around",
                alignItems: "center",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Calendar />
            </Box>
            <Box>
                <Line>
                    <Text a={"今日做题"} />
                    <Digit a={daily.question.used} />
                    <Text a={"道"} />
                </Line>
                <Line>
                    <Text a={"累积做题"} />
                    <Digit a={summary.question.total} />
                    <Text a={"道"} />
                </Line>
                <Line>
                    <Text a={"错题集"} />
                    <Digit a={summary.question.wrongs_count} />
                    <Text a={"道"} />
                </Line>
                <Line>
                    <Text a={"已掌握错题"} />
                    <Digit a={user.data.remove_corrects} />
                    <Text a={"道"} />
                </Line>
            </Box>
            <SimulateDisplay sim={summary.simulate} />
        </Stack>
    );
}

function SimulateDisplay(props: { sim: Simulate }) {
    const { sim } = props;
    return (
        <Box>
            <Line>
                <Text a={"累积模考"} />
                <Digit a={sim.used_count} />
                <Text a={"次"} />
            </Line>
            <AvgDisplay isV={true} allAvg={sim.all_avg_score.v} />
            <AvgDisplay isV={false} allAvg={sim.all_avg_score.q} />
        </Box>
    );
}

function AvgDisplay(props: { isV: boolean; allAvg: RateScore }) {
    const dispatch = useAppDispatch();

    const { isV, allAvg } = props;
    const login = useSelector(selectLogin);

    // user select
    const selectProps = {
        items: FilterScopeHomeItems,
        defaultValue: FilterScopeKind.All,
        variant: "standard",
        sx: {
            minWidth: 0,
            "& .MuiInputBase-input": {
                padding: "4px 7px",
                paddingLeft: 0,
                color: "#707070",
                fontWeight: 700,
                fontSize: "0.9rem",
            },
        },
        disabled: !login,
    } as SelectEnumValueProps;

    const [scopeDiv, scopeOrNull] = useSelectEnumValue(selectProps);
    const scope = scopeOrNull || 0;

    // api call
    const [apiAvg, setApiAvg] = useState(new RateScore());
    useEffect(() => {
        if (scope === FilterScopeKind.All) {
            return;
        }
        methodGetAvgScore(dispatch, { scope: scope, is_v: isV }).then((rsp) => setApiAvg(rsp.avg));
    }, [scope, dispatch, isV]);

    // calc avg
    const getAvg = () => {
        if (scope === FilterScopeKind.All) {
            return allAvg;
        }
        return apiAvg;
    };
    const avg = getAvg();

    return (
        <Line>
            {scopeDiv}
            <Text a={(isV ? "V" : "Q") + "平均"} />
            {avg && avg.rate > 0 ? (
                <>
                    <Digit a={avg && avg.rate.toFixed(0)} />
                    <Text a={"分"} />
                </>
            ) : (
                <Text a={<>&nbsp;无</>} />
            )}
        </Line>
    );
}

interface LineProps {}

function Line(props: PropsWithChildren<LineProps>) {
    const { children } = props;
    return <XStack sx={{ mt: "8px", mb: "8px" }}>{children}</XStack>;
}

export function Text(props: { a: any; color?: string }) {
    const { color, a } = props;
    return (
        <span
            style={{
                color: color ? color : "#707070",
                fontWeight: "bold",
                fontSize: "0.9rem",
            }}
        >
            {a}
        </span>
    );
}

export function Digit(props: { a: any }) {
    return (
        <>
            &nbsp;
            <span
                style={{
                    color: ColorOfficialRed,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                }}
            >
                {props.a}
            </span>
            &nbsp;
        </>
    );
}

function DakaLogo() {
    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    right: "10px",
                    top: "10px",
                    height: "35px",
                    width: "35px",
                    borderRadius: "50%",
                    background: Colors["primarya"],
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    right: "20px",
                    top: "20px",
                    fontSize: "1.2rem",
                }}
            >
                <b>学习日历</b>
            </Box>
        </>
    );
}
// 输入 enum列表
// 输出: 下拉框div, 选择的enum.value
export function useEnumValueSelectCustom(props: {
    items: EnumDisplay[];
    defaultValue?: number;
    title?: any;
    sx?: FormControlProps["sx"];
}): [any, number | undefined] {
    const { items, defaultValue, title, sx } = props;
    const [value, setValue] = useState(defaultValue !== undefined ? defaultValue.toString() : "");
    return [
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {title && <Box sx={{ mr: "10px", display: "flex" }}>{title}：</Box>}
            <FormControl size="small" variant="standard" sx={{ minWidth: "200px", ...sx }}>
                <Select
                    value={value}
                    onChange={(event: SelectChangeEvent) => {
                        setValue(event.target.value);
                    }}
                    size="small"
                    sx={{
                        "& .MuiSelect-select": {
                            marginRight: "1px",
                        },
                        "& .MuiOutlinedInput-input": {
                            marginRight: "1px",
                        },
                        "& .MuiInputBase-input": {
                            marginRight: "1px",
                        },
                        "& .MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input": {
                            marginRight: "1px",
                        },
                    }}
                >
                    {items.map((item, index) => (
                        <MenuItem key={index} value={item.value}>
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>,
        value.length === 0 ? undefined : parseInt(value),
    ];
}
